import { useState } from 'react';
import { formatFieldValue, parseDuration, type DurationValue } from '../../lib/fieldValue';
import type { Field } from '../../lib/templates';

interface DurationInputProps {
  field: Pick<Field, 'label'>;
  value: DurationValue | null;
  onCommit: (value: DurationValue | null) => void;
  disabled?: boolean;
}

function draftText(value: DurationValue | null): string {
  if (!value) {
    return '';
  }
  // formatFieldValue only needs `maxSeconds` off the config to decide "20s" vs "15-20s"; feeding
  // it the value's own bound keeps this in lockstep with `formatDuration` without duplicating it.
  return formatFieldValue(value, { type: 'DURATION', maxSeconds: value.maxSeconds ?? value.minSeconds });
}

/**
 * A single text field, formatted and parsed exactly like the sheet displays it: `"20s"`,
 * `"15-20s"`, `"30s *"` (the trailing `" *"` is this field's footnote — DURATION has no separate
 * checkbox because `parseDuration` already round-trips it). Live feedback: an unparsable, non-blank
 * string shows an inline error and does not commit; blank commits `null` (clears the cell).
 */
export function DurationInput({ field, value, onCommit, disabled = false }: DurationInputProps) {
  const [text, setText] = useState(() => draftText(value));
  const trimmed = text.trim();
  const parsed = trimmed === '' ? null : parseDuration(trimmed);
  const invalid = trimmed !== '' && parsed === null;

  function commit() {
    if (disabled) {
      return;
    }
    if (trimmed === '') {
      onCommit(null);
      return;
    }
    if (!parsed) {
      return;
    }
    onCommit({
      type: 'DURATION',
      minSeconds: parsed.minSeconds,
      maxSeconds: parsed.maxSeconds,
      footnote: parsed.footnote,
      note: value?.note ?? null,
    });
  }

  return (
    <label className="flex flex-col gap-1 text-sm text-text-muted">
      <span className="eyebrow">{field.label} (e.g. 15-20s, 30s *)</span>
      <input
        type="text"
        value={text}
        disabled={disabled}
        onChange={(event) => setText(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            commit();
          }
        }}
        placeholder="15-20s"
        aria-invalid={invalid}
        className={[
          'telemetry rounded-md border bg-bg px-3 py-2 text-text focus:border-accent',
          invalid ? 'border-signal' : 'border-border-strong',
        ].join(' ')}
      />
      {invalid && <span className="text-xs text-signal">Doesn&apos;t look like a duration (try &quot;15-20s&quot;).</span>}
    </label>
  );
}
